import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import {
  ChatCircleDots, Briefcase, CalendarBlank, UsersThree, Folder,
  ListChecks, UsersFour, Storefront, ChartLine, BookmarkSimple, Gear,
} from "@phosphor-icons/react";
import { AuthProvider } from "./lib/auth";
import { AuthGateProvider } from "./lib/authGate";
import ProtectedRoute from "./components/ProtectedRoute";
import PublicRoute from "./components/PublicRoute";
import CommandShell from "./components/CommandShell";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AuthCallback from "./pages/AuthCallback";
import SetupUsername from "./pages/SetupUsername";
import Admin from "./pages/Admin";
import HomeDashboard from "./pages/HomeDashboard";
import ModuleStub from "./pages/ModuleStub";
import TasksPage from "./pages/TasksPage";
import ProjectsList from "./pages/ProjectsList";
import ProjectWorkspace from "./pages/ProjectWorkspace";
import TeamsPage from "./pages/TeamsPage";
import MessagesPage from "./pages/MessagesPage";
import MeetupsPage from "./pages/MeetupsPage";
import EventsPage from "./pages/EventsPage";
import JobsPage from "./pages/JobsPage";
import BusinessesPage from "./pages/BusinessesPage";
import SettingsPage from "./pages/SettingsPage";
import PublicProfilePage from "./pages/PublicProfilePage";
import AdSuccess from "./pages/AdSuccess";
import FriendAccept from "./pages/FriendAccept";

// Any pre-Command-Center path lands on the new dashboard so old bookmarks
// and shared links stay useful.
function LegacyRedirect() { return <Navigate to="/app" replace />; }

// Small wrapper that pairs a Command Center route with the shared shell.
function Shell({ children }) { return <CommandShell>{children}</CommandShell>; }

// Module blurbs for the Phase 1 stubs. Each is authored so that the
// screen still communicates the vision even before the deep build.
const STUBS = {
  analytics: { title: "Analytics", icon: ChartLine,
    blurb: "Profile views, ad clicks, click-through rate, revenue analytics, campaign performance, and customer inquiries — one dashboard, business-wide clarity." },
  saved: { title: "Saved", icon: BookmarkSimple,
    blurb: "Everything you bookmark across HigherVision — jobs, events, meet-ups, businesses, products, messages, and posts — organized in one archive." },
};

function StubRoute({ moduleKey }) {
  const s = STUBS[moduleKey];
  return <ModuleStub title={s.title} blurb={s.blurb} icon={s.icon} testid={`stub-${moduleKey}`}/>;
}

function AppRouter() {
  const location = useLocation();
  // Handle OAuth callback FIRST (sync), before normal routing.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      {/* ── Public / entry pages ─────────────────────────────────── */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/setup-username" element={<ProtectedRoute><SetupUsername /></ProtectedRoute>} />

      {/* ── Command Center home ──────────────────────────────────── */}
      <Route path="/app"       element={<ProtectedRoute><Shell><HomeDashboard/></Shell></ProtectedRoute>} />
      <Route path="/app/home"  element={<Navigate to="/app" replace />} />

      {/* ── Deep-built modules ───────────────────────────────────── */}
      <Route path="/app/tasks"           element={<ProtectedRoute><Shell><TasksPage/></Shell></ProtectedRoute>} />
      <Route path="/app/projects"        element={<ProtectedRoute><Shell><ProjectsList/></Shell></ProtectedRoute>} />
      <Route path="/app/projects/:pid"   element={<ProtectedRoute><Shell><ProjectWorkspace/></Shell></ProtectedRoute>} />
      <Route path="/app/teams"           element={<ProtectedRoute><Shell><TeamsPage/></Shell></ProtectedRoute>} />
      <Route path="/app/teams/:tid"      element={<ProtectedRoute><Shell><TeamsPage/></Shell></ProtectedRoute>} />
      <Route path="/app/messages"        element={<ProtectedRoute><Shell><MessagesPage/></Shell></ProtectedRoute>} />
      <Route path="/app/meetups"         element={<ProtectedRoute><Shell><MeetupsPage/></Shell></ProtectedRoute>} />
      <Route path="/app/meetups/:mid"    element={<ProtectedRoute><Shell><MeetupsPage/></Shell></ProtectedRoute>} />
      <Route path="/app/events"          element={<ProtectedRoute><Shell><EventsPage/></Shell></ProtectedRoute>} />
      <Route path="/app/events/:eid"     element={<ProtectedRoute><Shell><EventsPage/></Shell></ProtectedRoute>} />
      <Route path="/app/jobs"            element={<ProtectedRoute><Shell><JobsPage/></Shell></ProtectedRoute>} />
      <Route path="/app/jobs/:jid"       element={<ProtectedRoute><Shell><JobsPage/></Shell></ProtectedRoute>} />
      <Route path="/app/businesses"      element={<ProtectedRoute><Shell><BusinessesPage/></Shell></ProtectedRoute>} />
      <Route path="/app/businesses/:bid" element={<ProtectedRoute><Shell><BusinessesPage/></Shell></ProtectedRoute>} />
      <Route path="/app/settings"        element={<ProtectedRoute><Shell><SettingsPage/></Shell></ProtectedRoute>} />
      <Route path="/app/u/:username"     element={<ProtectedRoute><Shell><PublicProfilePage/></Shell></ProtectedRoute>} />

      {/* ── Module stubs (Phase 2+ deep builds land here) ────────── */}
      {Object.keys(STUBS).map((k) => (
        <Route key={k}
               path={`/app/${k}`}
               element={<ProtectedRoute><Shell><StubRoute moduleKey={k}/></Shell></ProtectedRoute>}/>
      ))}

      {/* ── Standalone (no shell) ────────────────────────────────── */}
      <Route path="/app/ads/success" element={<ProtectedRoute><AdSuccess/></ProtectedRoute>} />
      <Route path="/app/friend/:token" element={<ProtectedRoute><FriendAccept/></ProtectedRoute>} />
      <Route path="/app/admin" element={<ProtectedRoute><Admin/></ProtectedRoute>} />

      {/* ── Legacy path redirects (old Instagram-style shell) ───── */}
      <Route path="/app/discover" element={<LegacyRedirect/>} />
      <Route path="/app/search" element={<LegacyRedirect/>} />
      <Route path="/app/chat" element={<Navigate to="/app/messages" replace />} />
      <Route path="/app/profile" element={<Navigate to="/app/settings" replace />} />
      <Route path="/app/profile/edit" element={<Navigate to="/app/settings" replace />} />
      <Route path="/app/subscription" element={<Navigate to="/app/settings" replace />} />
      <Route path="/app/create" element={<LegacyRedirect/>} />
      <Route path="/app/tools/business" element={<Navigate to="/app/businesses" replace />} />
      <Route path="/app/u/:username/reels" element={<LegacyRedirect/>} />
      <Route path="/app/u/:username/reels/:post_id" element={<LegacyRedirect/>} />
      <Route path="/app/biz/:username" element={<Navigate to="/app/businesses" replace />} />
      <Route path="/app/pass" element={<LegacyRedirect/>} />
      <Route path="/app/gigs" element={<LegacyRedirect/>} />
      <Route path="/app/news" element={<LegacyRedirect/>} />
      <Route path="/app/friends" element={<Navigate to="/app/teams" replace />} />
      <Route path="/app/leaderboard" element={<Navigate to="/app/analytics" replace />} />
      <Route path="/app/home/legacy" element={<LegacyRedirect/>} />
      <Route path="/app/tools" element={<LegacyRedirect/>} />
      <Route path="/app/tools/image" element={<LegacyRedirect/>} />
      <Route path="/app/tools/marketing" element={<LegacyRedirect/>} />
      <Route path="/app/tools/consultant" element={<LegacyRedirect/>} />
      <Route path="/app/tools/business/legacy" element={<LegacyRedirect/>} />
      <Route path="/app/tools/agent" element={<LegacyRedirect/>} />
      <Route path="/app/tools/ads" element={<LegacyRedirect/>} />
      <Route path="/app/tools/reporter" element={<LegacyRedirect/>} />
      <Route path="/app/tools/vision-chat" element={<LegacyRedirect/>} />
      <Route path="/app/tools/vision-chat/:code" element={<LegacyRedirect/>} />
      <Route path="/app/u/:username/legacy" element={<LegacyRedirect/>} />
      <Route path="/app/biz/:username/legacy" element={<LegacyRedirect/>} />
      <Route path="/business/:username" element={<LegacyRedirect/>} />
      <Route path="/event/:event_id" element={<LegacyRedirect/>} />
      <Route path="/gig/:gig_id" element={<LegacyRedirect/>} />
      <Route path="/split/:code" element={<LegacyRedirect/>} />

      {/* Anything else — nudge back to the Command Center. */}
      <Route path="*" element={<LegacyRedirect/>} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <AuthGateProvider>
              <AppRouter />
            </AuthGateProvider>
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}
